import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Download,
  MessageCircle,
  Monitor,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ClipboardPaste,
  Apple,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import {
  getSupportSession,
  joinSession,
  getMessages,
  sendMessage,
  submitRustdeskId,
  sendTypingIndicator,
  getLauncherUrl,
  SupportSession,
  ChatMessage,
} from '../services/supportSessionService';

type PageState = 'loading' | 'active' | 'expired' | 'error';

const POLL_INTERVAL = 4000;

// Detect platform
const detectPlatform = (): 'windows' | 'macos' | 'other' => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'macos';
  if (ua.includes('win')) return 'windows';
  return 'other';
};

const SupportSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const platform = detectPlatform();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [session, setSession] = useState<SupportSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [rustdeskId, setRustdeskId] = useState('');
  const [submittingRustdesk, setSubmittingRustdesk] = useState(false);
  const [rustdeskSubmitted, setRustdeskSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [clipboardError, setClipboardError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number>(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasJoinedRef = useRef(false);
  const lastTypingSentRef = useRef<number>(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load session on mount
  useEffect(() => {
    if (!sessionId) {
      setPageState('error');
      setErrorMessage('Invalid session link');
      return;
    }

    const loadSession = async () => {
      try {
        const data = await getSupportSession(sessionId);
        setSession(data);

        if (data.status === 'completed') {
          setPageState('expired');
          setErrorMessage('This support session has ended.');
          return;
        }
        if (data.status === 'expired') {
          setPageState('expired');
          setErrorMessage('This support session has expired.');
          return;
        }

        setPageState('active');

        // Join the session (notify technician)
        if (!hasJoinedRef.current) {
          hasJoinedRef.current = true;
          try {
            await joinSession(sessionId);
          } catch {
            // Already joined or session issue - non-critical
          }
        }

        // Load initial messages
        const msgs = await getMessages(sessionId);
        setMessages(msgs);
        if (msgs.length > 0) {
          lastMessageIdRef.current = msgs[msgs.length - 1].pk;
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Session not found';
        setPageState('expired');
        setErrorMessage(msg);
      }
    };

    loadSession();
  }, [sessionId]);

  // Poll for new messages
  const pollMessages = useCallback(async () => {
    if (!sessionId || pageState !== 'active') return;
    try {
      const newMsgs = await getMessages(sessionId, lastMessageIdRef.current || undefined);
      if (newMsgs.length > 0) {
        setMessages(prev => [...prev, ...newMsgs]);
        lastMessageIdRef.current = newMsgs[newMsgs.length - 1].pk;
      }

      // Also check session status
      const data = await getSupportSession(sessionId);
      setSession(data);
      if (data.status === 'completed' || data.status === 'expired') {
        setPageState('expired');
        setErrorMessage(data.status === 'completed' ? 'This support session has ended.' : 'This support session has expired.');
      }
    } catch {
      // Polling errors are non-critical
    }
  }, [sessionId, pageState]);

  useEffect(() => {
    if (pageState === 'active') {
      pollIntervalRef.current = setInterval(pollMessages, POLL_INTERVAL);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [pageState, pollMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || sending || !sessionId || !session) return;
    setSending(true);
    try {
      const newMsg = await sendMessage(sessionId, messageInput.trim(), session.customer_name);
      setMessages(prev => [...prev, newMsg]);
      lastMessageIdRef.current = newMsg.pk;
      setMessageInput('');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (!sessionId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current > 3000) {
      lastTypingSentRef.current = now;
      sendTypingIndicator(sessionId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSubmitRustdesk = async (id?: string) => {
    const idToSubmit = id || rustdeskId;
    if (!idToSubmit.trim() || submittingRustdesk || !sessionId) return;
    setSubmittingRustdesk(true);
    try {
      await submitRustdeskId(sessionId, idToSubmit.trim());
      setRustdeskSubmitted(true);
      setRustdeskId(idToSubmit.trim());
      toast.success('RustDesk ID sent to your technician!');
    } catch {
      toast.error('Failed to submit RustDesk ID');
    } finally {
      setSubmittingRustdesk(false);
    }
  };

  const handleDownload = (dlPlatform: 'windows' | 'macos' = 'windows') => {
    if (!sessionId) return;
    // Use native browser download (not fetch) — fetch has HTTP/2 framing
    // issues with streaming responses through the proxy.
    const url = getLauncherUrl(sessionId, dlPlatform);
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Show brief loading state while download starts
    setDownloading(true);
    setTimeout(() => setDownloading(false), 8000);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const cleaned = text.trim().replace(/\s+/g, ' ');
      if (cleaned && /^[\d\s]+$/.test(cleaned)) {
        // Looks like a RustDesk ID (digits and spaces)
        await handleSubmitRustdesk(cleaned);
      } else if (cleaned) {
        // Not obviously an ID - put it in the field and let user confirm
        setRustdeskId(cleaned);
        setShowManualInput(true);
        toast('Pasted from clipboard. Verify the ID and click Send.', { icon: 'i' });
      } else {
        toast.error('Clipboard is empty. Copy your ID from RustDesk first.');
      }
    } catch {
      setClipboardError(true);
      setShowManualInput(true);
      toast.error('Cannot access clipboard. Please type your ID manually.');
    }
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Connecting to support session...</p>
        </div>
      </div>
    );
  }

  // Error / Expired state
  if (pageState === 'expired' || pageState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Toaster position="top-right" />
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {pageState === 'error' ? (
              <AlertCircle className="w-8 h-8 text-red-500" />
            ) : (
              <Clock className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">
            {pageState === 'error' ? 'Session Not Found' : 'Session Ended'}
          </h1>
          <p className="text-gray-500">{errorMessage}</p>
          <p className="text-sm text-gray-400 mt-4">
            If you need help, please contact your technician for a new session link.
          </p>
        </div>
      </div>
    );
  }

  // Active state
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(to right, #4834d4, #341f97)' }}>
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">ITP360 Remote Support</h1>
              <p className="text-xs text-gray-500">Session for {session?.customer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Connected
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 max-w-6xl mx-auto w-full p-6 flex gap-6" style={{ minHeight: 0 }}>
        {/* Chat Panel */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-gray-700">Chat with your technician</span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Your technician will be with you shortly. Feel free to describe your issue here.
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.pk}
                  className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : msg.sender_type === 'system' ? 'justify-center' : 'justify-start'}`}
                >
                  {msg.sender_type === 'system' ? (
                    <div className="text-xs text-gray-400 italic py-1">{msg.content}</div>
                  ) : (
                    <div className={`max-w-[70%] rounded-xl px-4 py-2.5 ${
                      msg.sender_type === 'customer'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <div className="text-xs font-medium mb-0.5 opacity-75">{msg.sender_name}</div>
                      <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                      <div className={`text-[10px] mt-1 ${msg.sender_type === 'customer' ? 'text-indigo-200' : 'text-gray-400'}`}>
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-200 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => { setMessageInput(e.target.value); handleTyping(); }}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
              <button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || sending}
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 space-y-4 flex-shrink-0">
          {/* Step 1: Download Support Tool */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">1</div>
              <h3 className="text-sm font-semibold text-gray-700">Download Support Tool</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Download and open the file — it connects automatically.
            </p>
            <button
              onClick={() => handleDownload(platform === 'macos' ? 'macos' : 'windows')}
              disabled={downloading}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-70"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Building support tool...
                </>
              ) : (
                <>
                  {platform === 'macos' ? <Apple className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                  Download Support Tool
                </>
              )}
            </button>
          </div>

          {/* Step 2: Share your ID */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">2</div>
              <h3 className="text-sm font-semibold text-gray-700">Share Your ID</h3>
            </div>

            {rustdeskSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm font-medium text-green-700">ID sent to technician!</p>
                <p className="text-xs text-green-600 mt-1">
                  When they connect, click <strong>Accept</strong> in the RustDesk popup.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  {platform === 'windows'
                    ? 'If the automatic ID detection didn\'t work, copy the ID from the RustDesk window.'
                    : 'After opening RustDesk, copy the numeric ID shown at the top of the window.'}
                </p>

                {/* Primary: Paste from Clipboard */}
                {!clipboardError && (
                  <button
                    onClick={handlePasteFromClipboard}
                    disabled={submittingRustdesk}
                    className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submittingRustdesk ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ClipboardPaste className="w-4 h-4" />
                    )}
                    {submittingRustdesk ? 'Sending...' : 'Paste ID from Clipboard'}
                  </button>
                )}

                {/* Toggle manual input */}
                <button
                  onClick={() => setShowManualInput(!showManualInput)}
                  className="flex items-center justify-center gap-1 w-full text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Type ID manually
                  {showManualInput ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                {/* Manual input (tertiary fallback) */}
                {showManualInput && (
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      value={rustdeskId}
                      onChange={(e) => setRustdeskId(e.target.value)}
                      placeholder="Enter your RustDesk ID (e.g. 358 942 127)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none font-mono"
                    />
                    <button
                      onClick={() => handleSubmitRustdesk()}
                      disabled={!rustdeskId.trim() || submittingRustdesk}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submittingRustdesk ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {submittingRustdesk ? 'Sending...' : 'Send ID'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Help */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">How it works</h4>
            <ol className="text-xs text-gray-500 leading-relaxed space-y-1.5 list-decimal list-inside">
              <li>Click "Download Support Tool" above</li>
              <li>Open the downloaded file</li>
              <li>Your ID is sent to the technician automatically</li>
              <li>Your technician connects — no action needed</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportSessionPage;
